import React, { ReactNode, forwardRef, CSSProperties, useMemo } from "react";
import { getColorBaseTokenValue } from "../../design-tokens";
import styles from "./PageHeader.module.css";

export interface PageHeaderProps {
  /**
   * Título principal da página
   */
  title: string;

  /**
   * Descrição opcional da página
   */
  description?: string;

  /**
   * Botões de ação (máximo 2)
   */
  children?: ReactNode;

  /**
   * Classes CSS adicionais
   */
  className?: string;
}

/**
 * PageHeader - Componente de cabeçalho de página
 * 
 * Padroniza o topo das páginas do sistema com título, descrição e ações.
 * 
 * @example
 * ```tsx
 * <PageHeader 
 *   title="Operações"
 *   description="Gerencie as operações existentes no sistema"
 * >
 *   <Button label="Exportar" icon="file_export" severity="secondary" outlined />
 *   <Button label="Nova Operação" icon="add" severity="primary" />
 * </PageHeader>
 * ```
 */
export const PageHeader = forwardRef<HTMLDivElement, PageHeaderProps>(
  ({ title, description, children, className }, ref) => {
    const styleVariables = useMemo(
      (): CSSProperties => ({
        "--ds-pageheader-bg": getColorBaseTokenValue("color.neutral.000"),
        "--ds-pageheader-border": getColorBaseTokenValue("color.neutral.300"),
      } as CSSProperties),
      []
    );

    const containerClasses = [styles.container, className].filter(Boolean).join(" ");

    return (
      <div ref={ref} className={containerClasses} style={styleVariables}>
        <div className={styles.content}>
          <h1 className={styles.title}>{title}</h1>
          {description && <p className={styles.desc}>{description}</p>}
        </div>
        {children && <div className={styles.actions}>{children}</div>}
      </div>
    );
  }
);

PageHeader.displayName = "PageHeader";

export default PageHeader;
